import React, { useEffect, useState } from "react";
import { AutoSizer, Column, SortDirection, Table } from "react-virtualized";
import { getNumericComparator, getStringComparator, reverseOrder } from "@/utils/collect";
import styles from "@/components/LanguageModelArtifact/style.module.css";
import { getModelIcon } from "@/containers/ChatGPT/reducer";
import ModelStatus from "@/components/LanguageModelArtifact/Models/ModelStatus";
import * as MarketReducer from "containers/CommunityMarket/MarketReducer";
import * as fineTuningReducer from "containers/FineTuning/reducer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/aible/DropdownMenu/DropdownMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { isEnabled } from "@/utils/featureFlags";
import { useDispatch, useSelector } from "@/types/store";
import { toast } from "react-toastify";
const ModelTypeIcon = ({ model_id, source_type, name, source }) => {
    const icon = useSelector((state) => getModelIcon(state, model_id, source_type, name, source));
    return React.createElement("img", { className: styles.modelTableLogo, src: icon });
};
const TrainingJobsTable = ({ data, searchFilter }) => {
    const dispatch = useDispatch();
    const [sortBy, updateSortBy] = useState("createdDate");
    const [sortDirection, updateSortDirection] = useState(SortDirection.DESC);
    const [sortedData, updateSortedData] = useState(data);
    const [isLoading, updateIsLoading] = useState(false);
    const columns = [
        { label: "Name", dataKey: "name", width: 600, flexGrow: 1 },
        { label: "Source", dataKey: "source", width: 300 },
        { label: "Status", dataKey: "status", width: 200 },
        { label: "Added By", dataKey: "owner", width: 180 },
        { label: "Date Created", dataKey: "createdDate", width: 150 },
        { label: "", dataKey: "more", width: 40 }
    ];
    const getSortFunction = (sortBy) => {
        switch (sortBy) {
            case "createdDate":
                return getNumericComparator((x) => Date.parse(x[sortBy]));
            case "owner":
            case "name":
            case "model_id":
            case "status":
            case "source":
                return getStringComparator((x) => x[sortBy] ?? "");
            default:
                return getNumericComparator(() => 0);
        }
    };
    const sort = ({ sortBy, sortDirection }) => {
        const sortFunc = getSortFunction(sortBy);
        updateSortBy(sortBy);
        updateSortDirection(sortDirection);
        return updateSortedData(data.toSorted(sortDirection === SortDirection.ASC ? sortFunc : reverseOrder(sortFunc)));
    };
    useEffect(() => {
        sort({ sortBy, sortDirection });
    }, [data]);
    const filteredData = sortedData.filter((item) => searchFilter === "" || searchFilter === undefined
        ? item
        : item?.name?.toLowerCase()?.includes(searchFilter?.toLowerCase()));
    const rowHeight = 44;
    const tableHeight = filteredData?.length * rowHeight + 30;
    if (Boolean(searchFilter?.length) && filteredData?.length === 0) {
        return React.createElement("div", { className: styles.noMatchingText }, "No matching models found");
    }
    const handleDeleteModel = async (modelId) => {
        updateIsLoading(true);
        try {
            await dispatch(fineTuningReducer.deleteFineTuningExecution(modelId));
        }
        catch (error) {
            let message = error;
            if (error instanceof Error) {
                message = error.message;
            }
            toast(message, {
                type: toast.TYPE.ERROR,
                position: toast.POSITION.TOP_CENTER,
                autoClose: 7500
            });
        }
        finally {
            updateIsLoading(false);
        }
    };
    const handleCancelTrainModel = async (modelId) => {
        updateIsLoading(true);
        try {
            await dispatch(fineTuningReducer.cancelFineTuningExecution(modelId));
        }
        catch (error) {
            let message = error;
            if (error instanceof Error) {
                message = error.message;
            }
            toast(message, {
                type: toast.TYPE.ERROR,
                position: toast.POSITION.TOP_CENTER,
                autoClose: 7500
            });
        }
        finally {
            updateIsLoading(false);
        }
    };
    return (React.createElement("div", { className: styles.dataTableWrapper },
        React.createElement(AutoSizer, null, ({ width }) => (React.createElement(Table, { width: width, height: tableHeight, headerHeight: 30, rowHeight: rowHeight, rowCount: filteredData?.length, rowGetter: ({ index }) => filteredData[index], sort: sort, sortBy: sortBy, sortDirection: sortDirection, rowClassName: styles.fineTuningRow, className: styles.fineTuningTable, headerClassName: styles.fineTuningHeaderRow }, columns.map((column, index) => {
            if (column.dataKey === "name") {
                return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.fineTuningItem, cellRenderer: ({ rowData, cellData }) => (React.createElement("div", { className: styles.fineTuningItem },
                        React.createElement("div", { className: styles.modelLogoNameItem },
                            React.createElement(ModelTypeIcon, { ...rowData }),
                            cellData))) }));
            }
            if (column.dataKey === "source") {
                return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.fineTuningSourceItem }));
            }
            if (column.dataKey === "status") {
                return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.fineTuningItem, cellRenderer: ({ rowData }) => {
                        return (React.createElement("div", { className: styles.fineTuningItem },
                            React.createElement(ModelStatus, { status: rowData?.status })));
                    } }));
            }
            if (column.dataKey === "more") {
                return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.fineTuningSettings, cellRenderer: ({ rowData }) => {
                        return (React.createElement("div", { className: styles.fineTuningItemMore },
                            React.createElement(DropdownMenu, null,
                                React.createElement(DropdownMenuTrigger, { asChild: true },
                                    React.createElement(FontAwesomeIcon, { icon: faEllipsisVertical })),
                                React.createElement(DropdownMenuContent, { className: "w-56", align: "end" },
                                    ["RUNNING", "PENDING"].includes(rowData?.status) && (React.createElement(DropdownMenuItem, { onClick: () => {
                                            handleCancelTrainModel(rowData.llm.id);
                                        }, disabled: isLoading, className: "text-red-500" }, "Canceling Training")),
                                    ["SUCCEEDED", "FAILED"].includes(rowData?.status) && (React.createElement(DropdownMenuItem, { disabled: isLoading, onClick: () => {
                                            handleDeleteModel(rowData.llm.id);
                                        }, className: "text-red-500" }, "Delete Model")),
                                    isEnabled("community-market") && (React.createElement(DropdownMenuItem, { disabled: isLoading, onClick: () => {
                                            dispatch(MarketReducer.updatePublishItem(rowData.llm));
                                            dispatch(MarketReducer.updateShowPublishModal(true));
                                        } }, "Publish Model to Marketplace"))))));
                    } }));
            }
            return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.fineTuningItem }));
        }))))));
};
export default TrainingJobsTable;
